import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import {
  ISafeAny,
  ISearchParams,
  IViewPort,
  IConfig,
  ILabels,
  ITableMatrix,
  ITables,
  IScrollToLabelsParams,
  IScrollToTablesParams,
  IScrollToTableCellsParams,
  IHistoryChangeEvent,
  ICreateLabelsEvent,
  IDeleteLabelsEvent,
  IClickLabelsEvent,
  IMouseEnterLabelsEvent,
  IClickTablesEvent,
  ICreateTablesEvent,
  IDeleteTablesEvent,
  IMergeTablesEvent,
  IMouseEnterTablesEvent,
  IMouseLeaveTablesEvent,
  IUpdateTablesEvent,
  ISplitTablesEvent,
  IPdfScrollingEvent,
  IScrollToRectParams,
  IIRTable,
  IUpdateIRTablesEvent,
  IResizeLabelsEvent,
  IDragLabelsEvent,
  ICreateIRTableCellsEvent,
  IScrollToIRTableCellsParams,
  IMouseLeaveLabelsEvent,
  ISetLabelsParams,
  ISearchLabels,
  ISetShapesParams,
  IShapes,
  ICreateShapesEvent,
  IClickShapesEvent,
  IUpdateShapesEvent,
  IDeleteShapesEvent,
  IScrollToShapesParams,
  IHighLightTableCellsParams,
  IVisualPageJsonParams,
  IPdfRotation,
  IMagnifierChangeEvent,
  IScrollToPageParams,
  IShapeDomsChangeEvent,
  IViewTablesChangeEvent
} from './label.type';

export interface IOptions {
  dev?: boolean;
}

interface IEventProxy {
  method: string;
  args?: ISafeAny;
}

export class LabelHub {
  id: string;
  protected _dev = false;
  protected _api: ISafeAny;
  // config
  // 注意这里全部应该使用 BehaviorSubject 而非 Subject，如果使用 Subject 会碰到棘手的组件实例化相关的时序问题
  readonly config$ = new BehaviorSubject<Partial<IConfig> | null>(null);
  // load
  readonly loadedState$ = new BehaviorSubject(false);
  readonly loaded$ = this.loadedState$.pipe(filter(r => r === true));
  // ready
  readonly readyState$ = new BehaviorSubject(false);
  readonly ready$ = this.readyState$.pipe(filter(r => r === true));
  // pageNumber
  readonly pageNumber$ = new BehaviorSubject<number>(1);
  // pageCount
  readonly pageCount$ = new BehaviorSubject<number>(1);
  // magnifier
  readonly magnifierChange$ = new Subject<IMagnifierChangeEvent>();
  // viewports
  readonly viewportsChange$ = new Subject<IViewPort[]>();
  // zoom
  readonly zoom$ = new BehaviorSubject<number>(1);
  // events proxy
  readonly eventsAfterLoaded$ = new BehaviorSubject<IEventProxy | null>(null);
  readonly eventsAfterReady$ = new BehaviorSubject<IEventProxy | null>(null);
  // ++++++++++++++++++ Proxy Component Events Begin +++++++++++++++++++++++++++++
  readonly historyChange$: Subject<IHistoryChangeEvent> = new Subject();
  readonly pdfLoadFailed$ = new Subject();
  readonly pdfScrolling$ = new Subject<IPdfScrollingEvent>();
  // LABEL
  readonly createLabels$ = new Subject<ICreateLabelsEvent>();
  readonly deleteLabels$ = new Subject<IDeleteLabelsEvent>();
  readonly clickLabels$ = new Subject<IClickLabelsEvent>();
  readonly resizeLabels$ = new Subject<IResizeLabelsEvent>();
  readonly dragLabels$ = new Subject<IDragLabelsEvent>();
  readonly mouseEnterLabels$ = new Subject<IMouseEnterLabelsEvent>();
  readonly mouseLeaveLabels$ = new Subject<IMouseLeaveLabelsEvent>();
  // TABLE
  readonly viewTablesChange$ = new Subject<IViewTablesChangeEvent>();
  readonly createTables$ = new Subject<ICreateTablesEvent>();
  readonly deleteTables$ = new Subject<IDeleteTablesEvent>();
  readonly updateTables$ = new Subject<IUpdateTablesEvent>();
  readonly mergeTables$ = new Subject<IMergeTablesEvent>();
  readonly splitTables$ = new Subject<ISplitTablesEvent>();
  readonly clickTables$ = new Subject<IClickTablesEvent>();
  readonly mouseEnterTables$ = new Subject<IMouseEnterTablesEvent>();
  readonly mouseLeaveTables$ = new Subject<IMouseLeaveTablesEvent>();
  readonly updateIRTables$ = new Subject<IUpdateIRTablesEvent>();
  readonly createIRTableCells$ = new Subject<ICreateIRTableCellsEvent>();
  // SHAPE
  readonly createShapes$ = new Subject<ICreateShapesEvent>();
  readonly clickShapes$ = new Subject<IClickShapesEvent>();
  readonly updateShapes$ = new Subject<IUpdateShapesEvent>();
  readonly deleteShapes$ = new Subject<IDeleteShapesEvent>();
  readonly shapeDomsChange$ = new Subject<IShapeDomsChangeEvent>();
  // ++++++++++++++++++ Proxy Component Events End +++++++++++++++++++++++++++++

  constructor(
    options?: IOptions
  ) {
    this._dev = options?.dev || false;
  }

  get dev() {
    return this._dev;
  }

  get ready() {
    return this.readyState$.value;
  }

  get loaded() {
    return this.loadedState$.value;
  }

  get api() {
    return this._api;
  }

  get pageNumber() {
    return this.pageNumber$.value;
  }

  get pageCount() {
    return this.pageNumber$.value;
  }

  get zoom() {
    return this.zoom$.value;
  }

  /**  ++++++++++++++++++++++++ COMMON ++++++++++++++++++++++++ */
  config(options: Partial<IConfig>) {
    if (options.id) {
      this.id = options.id;
    }
    this.eventsAfterLoaded$.next({
      method: 'config',
      args: options
    });
  }

  setPageNumber(pageNumber: number) {
    this.eventsAfterReady$.next({
      method: 'setPageNumber',
      args: pageNumber
    });
  }

  setZoom(zoom: number) {
    this.eventsAfterReady$.next({
      method: 'setZoom',
      args: zoom
    });
  }

  setRotation(rotation: IPdfRotation) {
    this.eventsAfterReady$.next({
      method: 'setRotation',
      args: rotation
    });
  }

  search(params: ISearchParams): Observable<ISearchLabels> {
    return this.ready$.pipe(
      take(1),
      switchMap(() => {
        return this._api.search(params) as Observable<ISearchLabels>;
      })
    );
  }

  clearHistory() {
    this.eventsAfterReady$.next({
      method: 'clearHistory',
      args: null
    });
  }

  getOriginViewports(): Observable<IViewPort[]> {
    return this.ready$.pipe(
      take(1),
      switchMap(() => {
        return this._api.getOriginViewports() as Observable<IViewPort[]>;
      })
    );
  }

  getCurrentViewports(): Observable<IViewPort[]> {
    return this.ready$.pipe(
      take(1),
      switchMap(() => {
        return this._api.getCurrentViewports() as Observable<IViewPort[]>;
      })
    );
  }

  setApi(api: ISafeAny) {
    this._api = api;
  }

  redo() {
    this.eventsAfterReady$.next({
      method: 'redo'
    });
  }

  undo() {
    this.eventsAfterReady$.next({
      method: 'undo'
    });
  }

  scrollToRect(params: IScrollToRectParams) {
    this.eventsAfterReady$.next({
      method: 'scrollToRect',
      args: params
    });
  }

  scrollToPage(params: IScrollToPageParams) {
    this.eventsAfterReady$.next({
      method: 'scrollToPage',
      args: params
    });
  }

  scrollToSearchResult(params: IScrollToLabelsParams) {
    this.eventsAfterReady$.next({
      method: 'scrollToSearchResult',
      args: params
    });
  }

  getText() {
    return this.ready$.pipe(
      take(1),
      switchMap(() => {
        return this._api.getText() as Observable<string>;
      })
    );
  }

  setSearchResult(params: ILabels) {
    this.eventsAfterReady$.next({
      method: 'setSearchResult',
      args: params
    });
  }

  restore() {
    this.eventsAfterReady$.next({
      method: 'restore'
    });
  }

  /**  ++++++++++++++++++++++++ TEXT ++++++++++++++++++++++++ */
  setLabels(params: ISetLabelsParams) {
    this.eventsAfterReady$.next({
      method: 'setLabels',
      args: params
    });
  }

  getLabels(): Observable<ILabels> {
    return this.ready$.pipe(
      take(1),
      switchMap(() => {
        return this._api.getLabels() as Observable<ILabels>;
      })
    );
  }

  scrollToLabels(params: IScrollToLabelsParams) {
    this.eventsAfterReady$.next({
      method: 'scrollToLabels',
      args: params
    });
  }

  visualizePageJson(params: IVisualPageJsonParams) {
    this.eventsAfterReady$.next({
      method: 'visualizePageJson',
      args: params
    });
  }

  /**  ++++++++++++++++++++++++ TABLE ++++++++++++++++++++++++ */
  setTables(tables: ITables) {
    this.eventsAfterReady$.next({
      method: 'setTables',
      args: tables
    });
  }

  setPartialTables(tables: ITables) {
    this.eventsAfterReady$.next({
      method: 'setPartialTables',
      args: tables
    });
  }

  getTables(): Observable<ITables> {
    return this.ready$.pipe(
      take(1),
      switchMap(() => {
        return this._api.getTables() as Observable<ITables>;
      })
    );
  }

  getDirtyTablesMatrix(): Observable<ITableMatrix[]> {
    return this.ready$.pipe(
      take(1),
      switchMap(() => {
        return this._api.getDirtyTablesMatrix() as Observable<ITableMatrix[]>;
      })
    );
  }

  mergeCells() {
    this.eventsAfterReady$.next({
      method: 'mergeCells'
    });
  }

  splitCells() {
    this.eventsAfterReady$.next({
      method: 'splitCells'
    });
  }

  scrollToTables(params: IScrollToTablesParams) {
    this.eventsAfterReady$.next({
      method: 'scrollToTables',
      args: params
    });
  }

  scrollToTableCells(params: IScrollToTableCellsParams) {
    this.eventsAfterReady$.next({
      method: 'scrollToTableCells',
      args: params
    });
  }

  highLightTableCells(params: IHighLightTableCellsParams) {
    this.eventsAfterReady$.next({
      method: 'highLightTableCells',
      args: params
    });
  }

  /**  ++++++++++++++++++++++++ 不规则表格 ++++++++++++++++++++++++ */
  setIRTables(tables: IIRTable) {
    this.eventsAfterReady$.next({
      method: 'setIRTables',
      args: tables
    });
  }

  setIRPartialTables(tables: IIRTable) {
    this.eventsAfterReady$.next({
      method: 'setIRPartialTables',
      args: tables
    });
  }

  getIRTables(): Observable<IIRTable> {
    return this.ready$.pipe(
      take(1),
      switchMap(() => {
        return this._api.getIRTables() as Observable<IIRTable>;
      })
    );
  }

  getIRDirtyTablesMatrix(): Observable<ITableMatrix[]> {
    return this.ready$.pipe(
      take(1),
      switchMap(() => {
        return this._api.getIRDirtyTablesMatrix() as Observable<ITableMatrix[]>;
      })
    );
  }

  scrollToIRTableCells(params: IScrollToIRTableCellsParams) {
    this.eventsAfterReady$.next({
      method: 'scrollToIRTableCells',
      args: params
    });
  }

  setShapes(params: ISetShapesParams) {
    this.eventsAfterReady$.next({
      method: 'setShapes',
      args: params
    });
  }

  getShapes() {
    return this.ready$.pipe(
      take(1),
      switchMap(() => {
        return this._api.getShapes() as Observable<IShapes>;
      })
    );
  }

  scrollToShapes(params: IScrollToShapesParams) {
    this.eventsAfterReady$.next({
      method: 'scrollToShapes',
      args: params
    });
  }

  /**
  * 销毁 Hub
  * @description 不要在外部调用，这可能会产生不可预期的效果
  */
  destroy() {
    this.loadedState$.complete();
    this.readyState$.complete();
    this.pageNumber$.complete();
    this.pageCount$.complete();
    this.zoom$.complete();
    this.config$.complete();
    this.eventsAfterLoaded$.complete();
    this.eventsAfterReady$.complete();
    this.magnifierChange$.complete();

    // proxy events
    this.historyChange$.complete();
    this.pdfLoadFailed$.complete();
    this.pdfScrolling$.complete();
    this.createLabels$.complete();
    this.resizeLabels$.complete();
    this.dragLabels$.complete();
    this.deleteLabels$.complete();
    this.clickLabels$.complete();
    this.mouseEnterLabels$.complete();
    this.mouseLeaveLabels$.complete();
    this.mergeTables$.complete();
    this.splitTables$.complete();
    this.viewTablesChange$.complete();
    this.createTables$.complete();
    this.deleteTables$.complete();
    this.clickTables$.complete();
    this.mouseEnterTables$.complete();
    this.mouseLeaveTables$.complete();
    this.updateTables$.complete();
    this.updateIRTables$.complete();
    this.createIRTableCells$.complete();
    this.createShapes$.complete();
    this.clickShapes$.complete();
    this.updateShapes$.complete();
    this.deleteShapes$.complete();
  }
}
